import { useSyncExternalStore } from 'react';

/**
 * Where the wide sticky chat bar is drawn, when a screen gives it a place of its own.
 *
 * The bar is mounted once for the whole app (`FloatingChat` → `StickyChatBar`), fixed to the foot of the
 * content region. A screen that designs the bar into its own layout — the home's activity-map canvas, whose
 * frame sets the composer above its player band (Figma 2038:96187) — registers an element here, and the bar
 * portals into it for as long as it stays registered. It is the same bar either way: one instance, one
 * open/close state, one handoff to the chat.
 *
 * A store rather than a context because the bar is not a descendant of the screen that places it.
 */
let slot: HTMLElement | null = null;
const listeners = new Set<() => void>();

/** A ref callback for the element the bar should be drawn into; `null` hands it back to its fixed spot. */
export function setStickyChatSlot(el: HTMLElement | null): void {
  if (slot === el) return;
  slot = el;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const read = () => slot;
const readServer = () => null;

/** The registered element, or `null` when the bar keeps its fixed spot. */
export function useStickyChatSlot(): HTMLElement | null {
  return useSyncExternalStore(subscribe, read, readServer);
}
